import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { authFetch } from "../utils/authFetch";
import { BASE_URL } from "../utils/config";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { ToastContainer } from "react-toastify";

import styles from "./CheckoutSummaryPage.module.css";

const CheckoutSummaryPage = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, isAuthenticated } = useAuth();

  const selectedPlan = location.state?.selectedPlan;
  const selectedPrice = location.state?.selectedPrice;
  const activeTab = location.state?.activeTab;
  const discountInfo = location.state?.discountInfo; // Get discount information

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("stripe");
  const [processingPayment, setProcessingPayment] = useState(false);

  // Helper function to format CHF prices with apostrophe thousand separators (Swiss format)
  const formatCHF = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) return value;

    // Format with Swiss CHF format (apostrophe as thousand separator)
    // Example: 500000 → 500'000.00
    const formatted = numberValue.toFixed(2);
    const parts = formatted.split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'");
    return `${integerPart}.${parts[1]}`;
  };

  useEffect(() => {
    console.log("CheckoutSummaryPage received:", {
      selectedPlan,
      selectedPrice,
      activeTab,
    });

    // Check if user is already subscribed
    if (currentUser && currentUser.isSubscribed) {
      toast.info(t("already_subscribed_toast") || "You already have an active subscription.");
      // Redirect based on user type
      if (currentUser.userType === "demand") {
        navigate("/MySearch");
      } else if (currentUser.userType === "offer") {
        navigate("/MyProperties");
      } else {
        navigate("/");
      }
      return;
    }

    if (!selectedPlan) {
      toast.error(t("no_plan_selected_toast"));
      navigate("/subscription");
    }
    if (!isAuthenticated) {
      toast.error(t("login_required_toast"));
      navigate("/auth/login");
    }
  }, [selectedPlan, selectedPrice, activeTab, isAuthenticated, currentUser, navigate, t]);

  if (!selectedPlan || !isAuthenticated) {
    return (
      <div className={styles.redirectingContainer}>
        <div className={styles.redirectingText}>{t("redirecting")}</div>
      </div>
    );
  }

  const getPlanBgColor = (name) => {
    switch (name.toLowerCase()) {
      case "bronze member":
        return "#f3f3f3";
      case "silver member":
        return "#b6d9d3";
      case "gold member":
        return "#f3f3f3";
      case "platinum member":
        return "#b6d9d3";
      default:
        return "#ffffff";
    }
  };

  const handleProceedToPayment = async () => {
    if (!selectedPaymentMethod) {
      toast.error(t("select_payment_method_toast"));
      return;
    }

    setProcessingPayment(true);

    try {
      const userEmail = currentUser.email;
      
      // Determine interval: check selectedPrice first, then selectedPlan, then fallback to activeTab
      // This function is used by both Stripe and PayPal payment flows
      const getInterval = () => {
        // First check selectedPrice interval (must be "month" or "year")
        if (selectedPrice?.interval && (selectedPrice.interval === "month" || selectedPrice.interval === "year")) {
          return selectedPrice.interval;
        }
        // Then check selectedPlan interval (must be "month" or "year")
        if (selectedPlan?.interval && (selectedPlan.interval === "month" || selectedPlan.interval === "year")) {
          return selectedPlan.interval;
        }
        // Finally fallback to activeTab
        return activeTab === "yearly" ? "year" : "month";
      };
      
      // Get the correct price object based on the determined interval
      // This ensures we use the correct price (monthly or yearly) with all its properties
      const getPriceForInterval = () => {
        const interval = getInterval();
        
        // Find the price from selectedPlan.prices that matches the interval
        if (selectedPlan?.prices && Array.isArray(selectedPlan.prices)) {
          const matchingPrice = selectedPlan.prices.find(
            (p) => p.interval === interval && p.isActive
          ) || selectedPlan.prices.find((p) => p.interval === interval);
          
          if (matchingPrice) {
            return matchingPrice;
          }
        }
        
        // Fallback to selectedPrice if it matches the interval
        if (selectedPrice?.interval === interval) {
          return selectedPrice;
        }
        
        // Final fallback to selectedPrice
        return selectedPrice;
      };
      
      // Get amount based on the determined interval
      // This ensures the amount matches the interval (monthly or yearly)
      const getAmount = () => {
        // If discount is applicable, use discounted price
        if (discountInfo?.hasDiscount) {
          return discountInfo.discountedPrice;
        }
        
        const priceForInterval = getPriceForInterval();
        
        // Use amount from the correct price object
        if (priceForInterval?.amount) {
          return priceForInterval.amount;
        }
        
        // Final fallback
        return selectedPrice?.amount || selectedPlan?.price;
      };
      
      if (selectedPaymentMethod.toLowerCase() == "stripe") {
        const stripeLocaleMap = {
          en: "en",
          de: "de",
          fr: "fr",
          it: "it",
          es: "es",
          nl: "nl",
          pt: "pt",
          zh: "zh",
        };
        const stripeLocale =
          stripeLocaleMap[i18n.language] || "auto"; // ✅ safe default

        // Determine interval and get the correct price object
        const interval = getInterval();
        const priceForInterval = getPriceForInterval();
        const amount = getAmount();
        
        // Get the Stripe price ID from the correct price object
        const stripePriceId = priceForInterval?.stripePriceId || priceForInterval?.stripe_price_id || priceForInterval?.id;
        
        const stripePayload = {
          planId: selectedPlan?.SubscriptionProviders.find(
            (p) => p.paymentGateway.toLowerCase() === "stripe"
          )?.planId,
          priceId: priceForInterval?.id, // ✅ Internal price ID from the correct interval price
          stripePriceId: stripePriceId, // ✅ Stripe-specific price ID from the correct interval price
          amount: amount, // ✅ Amount based on determined interval (monthly or yearly)
          userId: currentUser.id,
          email: userEmail,
          platform: "web",
          locale: stripeLocale,
          interval: interval, // ✅ Send interval to ensure correct billing (checks selectedPrice, selectedPlan, then activeTab)
          successUrl: `${window.location.origin}/subscription?success=true&paymentGateway=stripe`,
          cancelUrl: `${window.location.origin}/subscription?cancel=true`,
          ...(discountInfo?.hasDiscount && {
            discountPercentage: discountInfo.discountPercentage,
            originalPrice: discountInfo.originalPrice,
            discountedPrice: discountInfo.discountedPrice,
          }),
        };
        
        console.log("=== Stripe Payment Debug Info ===");
        console.log("Initiating Stripe payment with payload:", stripePayload);
        console.log("Determined Interval:", interval);
        console.log("Price Object for Interval:", priceForInterval);
        console.log("Original Selected Price:", selectedPrice);
        console.log("Selected Price Details:", {
          originalPriceId: selectedPrice?.id,
          originalInterval: selectedPrice?.interval,
          originalAmount: selectedPrice?.amount,
          activeTab: activeTab,
        });
        console.log("Corrected Price Details (for interval):", {
          priceId: priceForInterval?.id,
          stripePriceId: stripePriceId,
          amount: amount,
          interval: interval,
        });
        console.log("Selected Plan Details:", {
          planId: selectedPlan?.id,
          planName: selectedPlan?.name,
          stripePlanId: selectedPlan?.SubscriptionProviders?.find(
            (p) => p.paymentGateway.toLowerCase() === "stripe"
          )?.planId,
          availablePrices: selectedPlan?.prices?.map(p => ({ id: p.id, interval: p.interval, amount: p.amount }))
        });
        console.log("================================");

        const response = await authFetch(
          `${BASE_URL}/subscription/create-stripe`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(stripePayload),
          }
        );
        const data = await response.json();

        if (response.ok && data.url) {
          localStorage.setItem(
            "pendingSubscription",
            JSON.stringify({
              planId: selectedPlan.id,
              paymentGateway: "stripe",
            })
          );
          console.log("Redirecting to Stripe checkout.");
          window.location.href = data.url;
        } else {
          console.error(
            "Stripe session creation failed. Backend response:",
            data
          );
          toast.error(data.message || t("stripe_payment_failed_toast"));
        }
      }
    } catch (error) {
      console.error("Error during payment initiation:", error);
      toast.error(t("network_error_payment_toast"));
    } finally {
      setProcessingPayment(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerSection}>
        <h2 className={styles.checkoutHeading}>{t("Checkout")}</h2>
        <h1 className={styles.confirmSubscriptionHeading}>
          {t("Confirm")} {t("Subscribe_Your")} {t("Membership")}
        </h1>
      </div>

      <div
        className={styles.planSummaryCard}
        style={{ backgroundColor: getPlanBgColor(selectedPlan.name) }}
      >
        <h3 className={styles.planName}>{selectedPlan.name}</h3>
        {discountInfo?.hasDiscount ? (
          <div>
            <p className={styles.planPrice} style={{ textDecoration: "line-through", color: "#999", fontSize: "16px", marginBottom: "5px" }}>
              CHF {formatCHF(discountInfo.originalPrice)} / {selectedPrice?.interval === "month" ? t("monthly") : selectedPrice?.interval === "year" ? t("yearly") : selectedPrice?.interval || selectedPlan.interval}
            </p>
            <p className={styles.planPrice} style={{ color: "#00aa8f", fontSize: "24px", fontWeight: "bold" }}>
              CHF {formatCHF(discountInfo.discountedPrice)} / {selectedPrice?.interval === "month" ? t("monthly") : selectedPrice?.interval === "year" ? t("yearly") : selectedPrice?.interval || selectedPlan.interval}
            </p>
            <p style={{ color: "#FF69B4", fontWeight: "bold", marginTop: "5px" }}>
              {Math.round(discountInfo.discountPercentage)}% {t("off")}
            </p>
          </div>
        ) : (
          <p className={styles.planPrice}>
            CHF {formatCHF(selectedPrice?.amount || selectedPlan.price)} /{" "}
            {selectedPrice?.interval === "month" ? t("monthly") : selectedPrice?.interval === "year" ? t("yearly") : selectedPrice?.interval || selectedPlan.interval}
          </p>
        )}
        {/* <p className={styles.planDescription}>
          {(() => {
            // Parse description to show only relevant section (monthly or yearly)
            if (!selectedPlan.description) return "";
            const isYearly = activeTab === "yearly";
            const targetType = isYearly ? "yearly" : "monthly";
            const sections = selectedPlan.description.split(/\n+/);
            const targetSection = sections.find(section =>
              section.toLowerCase().includes(`for ${targetType}`)
            ) || selectedPlan.description;
            return targetSection.trim();
          })()}
        </p> */}
      </div>

      <div className={styles.paymentMethodCard}>
        <h3 className={styles.choosePaymentHeading}>
          {t("Choose Payment Method")}
        </h3>
        <div className={styles.paymentOptions}>
          <label
            className={`${styles.paymentOptionLabel} ${selectedPaymentMethod.toLowerCase() == "stripe"
                ? styles.selectedPaymentOption
                : ""
              }`}
          >
            <input
              type="radio"
              name="paymentMethod"
              value="stripe"
              checked={selectedPaymentMethod.toLowerCase() == "stripe"}
              onChange={() => setSelectedPaymentMethod("stripe")}
              className={styles.radioInput}
            />
            <span className={styles.paymentMethodText}>{t("credit_card")}</span>
          </label>
        </div>
      </div>

      <div className={styles.buttonContainer}>
        <button
          onClick={handleProceedToPayment}
          disabled={processingPayment || !selectedPaymentMethod}
          className={styles.proceedButton}
        >
          {processingPayment ? t("processing") : t("Next")}
        </button>
      </div>
      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default CheckoutSummaryPage;
